import {
  CalendarPlus,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  LinkIcon,
  RefreshCcw,
  Trash2,
} from 'lucide-react';
import { useState, type DragEvent, type ReactElement } from 'react';
import {
  Button,
  CardSurface,
  EmptyState,
  IconButton,
  Notice,
  Skeleton,
  TextField,
} from '@/components';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  effectiveTimezone,
  weekStartForLocale,
  type WeekStart,
} from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import type {
  CalendarProvider,
  ConnectedCalendarProvider,
  Planner,
  PlannerFocusBlock,
  PlannerLinkedCard,
  PlannerPreferences,
  PlannerSchedulableCard,
  PlannerSuggestion,
  PlannerSuggestions,
  PlannerWeekday,
} from '@molt/shared';

export interface PlannerPageProps {
  readonly planner: Planner | undefined;
  readonly suggestions?: PlannerSuggestions | undefined;
  readonly visibleDays: readonly PlannerDay[];
  readonly loading?: boolean | undefined;
  readonly error?: string | null | undefined;
  readonly busyAction?: string | null | undefined;
  readonly mutationError?: string | null | undefined;
  readonly onPreviousRange: () => void;
  readonly onNextRange: () => void;
  readonly onToday: () => void;
  readonly onRetry: () => void;
  readonly onBoardFiltersChange: (boardIds: readonly string[]) => void;
  readonly onIncludeUnassignedChange: (includeUnassigned: boolean) => void;
  readonly onWorkingHoursChange: (
    workingHours: PlannerPreferences['workingHours'],
  ) => void;
  readonly onWorkingDaysChange: (workingDays: readonly PlannerWeekday[]) => void;
  readonly onStartCalendarConnection: (provider: CalendarProvider) => void;
  readonly onToggleCalendarDisplayed: (calendarId: string, displayed: boolean) => void;
  readonly onDisconnectCalendarAccount: (accountId: string) => void;
  readonly onCreateFocusBlock: (cardId: string, startAt: string, endAt: string) => void;
  readonly onMoveFocusBlock: (
    focusBlockId: string,
    startAt: string,
    endAt: string,
  ) => void;
  readonly onResizeFocusBlock: (
    focusBlockId: string,
    startAt: string,
    endAt: string,
  ) => void;
  readonly onLinkCardToFocusBlock: (focusBlockId: string, cardId: string) => void;
  readonly onRemoveLinkedCard: (focusBlockId: string, linkId: string) => void;
  readonly onDeleteFocusBlock: (focusBlockId: string) => void;
  readonly onCompleteCard: (
    cardId: string,
    boardId: string | null,
    completed: boolean,
  ) => void;
  // `FR-NOTIF-025` constrains *proposals* by source board, which is a different
  // clause from `FR-NOTIF-020`'s board filter over *the panel* — so the ids the
  // Smart-schedule panel collects travel on the request rather than through
  // `onBoardFiltersChange`. `undefined` means "no proposal-scoped constraint",
  // which is the byte-for-byte shipped request; the field is omitted and the
  // server falls back to the stored filter.
  readonly onRequestSuggestions: (sourceBoardIds: readonly string[] | undefined) => void;
  readonly onAcceptSuggestion: (suggestion: PlannerSuggestion) => void;
  readonly onRejectSuggestion: (proposalId: string) => void;
}

export interface PlannerDay {
  readonly key: string;
  readonly label: string;
  readonly shortLabel: string;
  readonly startAt: string;
}

type InterfaceTranslate = ReturnType<typeof useInterfaceLanguage>['t'];

/**
 * Canonical Sunday-first order, indexed to match `Date#getDay` -- NOT the order
 * anything renders in. `orderedWeekdays` rotates it by the member's week start,
 * so this array exists to be rotated, and that rotation is its only reader.
 */
const WEEKDAYS_FROM_SUNDAY: readonly PlannerWeekday[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/**
 * `FR-NOTIF-032` -- "A per-user language setting drives date, time and calendar
 * formatting: ... and first day of week." So the working-days picker is ordered
 * by the member's language rather than by a hard-coded literal, and the week
 * start is DERIVED (`weekStartForLocale`) rather than stored: the same
 * requirement's edge case rules out a second setting beside the language.
 *
 * Rotating one canonical array keeps the seven days in weekly sequence for
 * either start -- Monday-first yields Mon..Sun, Sunday-first yields Sun..Sat --
 * which a pair of hand-written literals would not guarantee.
 *
 * The picker reflows and nothing is added beside it to explain the reflow: the
 * week simply starts where the member's language says it starts.
 */
function orderedWeekdays(weekStart: WeekStart): readonly PlannerWeekday[] {
  return [
    ...WEEKDAYS_FROM_SUNDAY.slice(weekStart),
    ...WEEKDAYS_FROM_SUNDAY.slice(0, weekStart),
  ];
}

// Both board checkbox groups offer the same board titles — FR-NOTIF-020 filters
// the panel, FR-NOTIF-025 constrains proposals — so each is named by the heading
// of the panel it belongs to. Without that, "Launch board" is two controls with
// one accessible name and two different meanings.
const BOARD_FILTERS_HEADING_ID = 'planner-board-filters-heading';
const SMART_SCHEDULE_HEADING_ID = 'planner-smart-schedule-heading';
const SMART_SCHEDULE_BOARDS_LEGEND_ID = 'planner-smart-schedule-boards-legend';

const SLOT_HOURS = [8, 9, 10, 11, 13, 14, 15, 16] as const;
const PLANNER_CARD_DRAG_TYPE = 'application/x-molt-planner-card-id';

export function PlannerPage({
  planner,
  suggestions,
  visibleDays,
  loading = false,
  error = null,
  busyAction = null,
  mutationError = null,
  onPreviousRange,
  onNextRange,
  onToday,
  onRetry,
  onBoardFiltersChange,
  onIncludeUnassignedChange,
  onWorkingHoursChange,
  onWorkingDaysChange,
  onStartCalendarConnection,
  onToggleCalendarDisplayed,
  onDisconnectCalendarAccount,
  onCreateFocusBlock,
  onMoveFocusBlock,
  onResizeFocusBlock,
  onLinkCardToFocusBlock,
  onRemoveLinkedCard,
  onDeleteFocusBlock,
  onCompleteCard,
  onRequestSuggestions,
  onAcceptSuggestion,
  onRejectSuggestion,
}: PlannerPageProps): ReactElement {
  const { locale, t } = useInterfaceLanguage();
  const localePreferences = useLocalePreferencesValue();
  const plannerTimeZone = effectiveTimezone(localePreferences);
  const boardOptions = plannerBoardOptions(planner);
  const selectedBoardIds =
    planner === undefined
      ? new Set<string>()
      : new Set(planner.selectedBoardFilters.map((filter) => filter.boardId));
  const effectiveSelected =
    planner?.selectedBoardFilters.length === 0
      ? new Set(boardOptions.map((board) => board.id))
      : selectedBoardIds;
  const firstSchedulable = planner?.schedulableCards[0];
  const firstFocusBlock = planner?.focusBlocks[0];
  const defaultFocusDayKey = visibleDays[0]?.key;
  const defaultFocusTime = formatHour(10, locale);
  const rangeLabel =
    visibleDays.length === 0
      ? t('planner.title')
      : `${visibleDays[0]?.shortLabel ?? ''}${
          visibleDays.length > 1
            ? ` - ${visibleDays[visibleDays.length - 1]?.shortLabel ?? ''}`
            : ''
        }`;

  function toggleBoard(boardId: string): void {
    const next = new Set(effectiveSelected);
    if (next.has(boardId)) {
      next.delete(boardId);
    } else {
      next.add(boardId);
    }
    onBoardFiltersChange([...next]);
  }

  return (
    <main className="mx-auto flex w-full max-w-[96rem] flex-col gap-5 px-4 py-6 sm:px-6">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-sm font-medium text-ink-muted">{t('planner.description')}</p>
          <h1 className="font-display text-3xl font-semibold text-ink">
            {t('planner.title')}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            iconStart={<ChevronLeft className="size-4" />}
            onClick={onPreviousRange}
          >
            {t('planner.previous')}
          </Button>
          <Button variant="secondary" size="sm" onClick={onToday}>
            {t('planner.today')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            iconStart={<ChevronRight className="size-4" />}
            onClick={onNextRange}
          >
            {t('planner.next')}
          </Button>
          <span className="text-sm font-semibold text-ink">{rangeLabel}</span>
        </div>
      </header>

      {error !== null ? (
        <EmptyState
          tone="error"
          size="panel"
          title={t('planner.loadFailedTitle')}
          body={t('planner.genericError')}
          action={
            <Button variant="secondary" onClick={onRetry}>
              {t('planner.retry')}
            </Button>
          }
        />
      ) : null}
      {mutationError !== null ? (
        <Notice tone="bad" body={t('planner.genericError')} />
      ) : null}

      {loading ? (
        <div aria-busy="true" className="grid gap-3 lg:grid-cols-[20rem_1fr]">
          <Skeleton shape="block" />
          <Skeleton shape="block" count={2} />
        </div>
      ) : planner === undefined ? null : (
        <div className="grid min-h-[42rem] gap-4 lg:grid-cols-[21rem_1fr]">
          <aside className="flex flex-col gap-4">
            <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
              <div>
                <h2 className="text-base font-semibold text-ink">
                  {t('planner.schedulable.title')}
                </h2>
                <p className="text-sm text-ink-muted">
                  {t('planner.schedulable.description')}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--color-accent)]"
                  checked={planner.preferences.includeUnassigned}
                  onChange={(event) =>
                    onIncludeUnassignedChange(event.currentTarget.checked)
                  }
                />
                {t('planner.schedulable.includeUnassigned')}
              </label>
              {planner.schedulableCards.length === 0 ? (
                <EmptyState
                  tone="neutral"
                  size="inline"
                  headingLevel={3}
                  title={t('planner.schedulable.emptyTitle')}
                  body={t('planner.schedulable.emptyBody')}
                />
              ) : (
                <ul
                  className="flex flex-col gap-2"
                  aria-label={t('planner.schedulable.list')}
                >
                  {planner.schedulableCards.map((item) => (
                    <PlannerCardRow
                      key={`${item.source}-${item.card.id}`}
                      item={item}
                      busy={busyAction === item.card.id}
                      onCreateFocusBlock={(startAt, endAt) =>
                        onCreateFocusBlock(item.card.id, startAt, endAt)
                      }
                      defaultFocusDayKey={defaultFocusDayKey}
                      defaultFocusTime={defaultFocusTime}
                      onCompleteCard={onCompleteCard}
                      locale={locale}
                      timeZone={plannerTimeZone}
                    />
                  ))}
                </ul>
              )}
            </CardSurface>

            <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
              <h2
                id={BOARD_FILTERS_HEADING_ID}
                className="text-base font-semibold text-ink"
              >
                {t('planner.boardFilters.title')}
              </h2>
              {boardOptions.length === 0 ? (
                <p className="text-sm text-ink-muted">
                  {t('planner.boardFilters.empty')}
                </p>
              ) : (
                // FR-NOTIF-020's filter over *the panel*. Named by its own
                // heading, because FR-NOTIF-025's proposal-scoped list in the
                // Smart-schedule panel offers the same board titles and the two
                // must not be one ambiguous set of checkboxes.
                <div
                  role="group"
                  aria-labelledby={BOARD_FILTERS_HEADING_ID}
                  className="grid gap-2"
                >
                  {boardOptions.map((board) => (
                    <label
                      key={board.id}
                      className="flex items-center gap-2 rounded-card border border-border-strong bg-surface px-3 py-2 text-sm text-ink"
                    >
                      <input
                        type="checkbox"
                        className="size-4 accent-[var(--color-accent)]"
                        checked={effectiveSelected.has(board.id)}
                        onChange={() => toggleBoard(board.id)}
                      />
                      <span className="min-w-0 flex-1 truncate">{board.title}</span>
                    </label>
                  ))}
                </div>
              )}
            </CardSurface>

            <PlannerPreferencesPanel
              preferences={planner.preferences}
              onWorkingHoursChange={onWorkingHoursChange}
              onWorkingDaysChange={onWorkingDaysChange}
            />

            <PlannerCalendarAccountsPanel
              planner={planner}
              onStartCalendarConnection={onStartCalendarConnection}
              onToggleCalendarDisplayed={onToggleCalendarDisplayed}
              onDisconnectCalendarAccount={onDisconnectCalendarAccount}
            />

            <PlannerSuggestionsPanel
              planner={planner}
              suggestions={suggestions}
              busy={busyAction === 'suggestions'}
              timeZone={plannerTimeZone}
              onRequestSuggestions={onRequestSuggestions}
              onAcceptSuggestion={onAcceptSuggestion}
              onRejectSuggestion={onRejectSuggestion}
            />
          </aside>

          <section
            aria-label={t('planner.rangeCalendar', { range: rangeLabel })}
            className={cn(
              'grid min-w-0 gap-3 overflow-x-auto pb-2',
              visibleDays.length <= 1
                ? 'grid-cols-1'
                : visibleDays.length <= 3
                  ? 'md:grid-cols-3'
                  : 'md:grid-cols-[repeat(7,minmax(14rem,1fr))]',
            )}
          >
            {visibleDays.map((day) => (
              <PlannerDayColumn
                key={day.key}
                day={day}
                planner={planner}
                firstSchedulable={firstSchedulable}
                firstFocusBlock={firstFocusBlock}
                busyAction={busyAction}
                locale={locale}
                onCreateFocusBlock={onCreateFocusBlock}
                onMoveFocusBlock={onMoveFocusBlock}
                onResizeFocusBlock={onResizeFocusBlock}
                onLinkCardToFocusBlock={onLinkCardToFocusBlock}
                onRemoveLinkedCard={onRemoveLinkedCard}
                onDeleteFocusBlock={onDeleteFocusBlock}
                onCompleteCard={onCompleteCard}
                timeZone={plannerTimeZone}
              />
            ))}
          </section>
        </div>
      )}
    </main>
  );
}

function PlannerCardRow({
  item,
  busy,
  defaultFocusDayKey,
  defaultFocusTime,
  locale,
  timeZone,
  onCreateFocusBlock,
  onCompleteCard,
}: {
  readonly item: PlannerSchedulableCard;
  readonly busy: boolean;
  readonly defaultFocusDayKey: string | undefined;
  readonly defaultFocusTime: string;
  readonly locale: string;
  readonly timeZone: string;
  readonly onCreateFocusBlock: (startAt: string, endAt: string) => void;
  readonly onCompleteCard: (
    cardId: string,
    boardId: string | null,
    completed: boolean,
  ) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const due = formatDateTime(item.card.dueAt, locale, timeZone);
  const title = cardTitle(item.card.title, t);
  const boardId = item.source === 'board' ? item.board.id : null;
  const startAt = instantForDateKeyHour(
    defaultFocusDayKey ?? dateKeyInZone(item.card.dueAt, timeZone),
    10,
    timeZone,
  );
  const endAt = addMinutes(startAt, 60);

  return (
    <li
      aria-label={t('planner.schedulable.cardLabel', { card: title })}
      className="cursor-grab rounded-card border border-border bg-surface-raised p-3 shadow-card active:cursor-grabbing"
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData(PLANNER_CARD_DRAG_TYPE, item.card.id);
        event.dataTransfer.setData('text/plain', item.card.id);
      }}
    >
      <div className="flex flex-col gap-2">
        <div>
          <p className="text-sm font-semibold text-ink">{title}</p>
          <p className="text-xs text-ink-muted">
            {item.source === 'board'
              ? `${item.board.title} / ${item.list.title}`
              : t('planner.source.inbox')}
          </p>
          <p className="text-xs text-ink-muted">{t('planner.dueDate', { date: due })}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            iconStart={<CalendarDays className="size-4" />}
            onClick={() => onCreateFocusBlock(startAt, endAt)}
          >
            {t('planner.scheduleAt', { time: defaultFocusTime })}
          </Button>
          {item.card.canComplete ? (
            <Button
              variant={item.card.completed ? 'secondary' : 'primary'}
              size="sm"
              iconStart={<Check className="size-4" />}
              onClick={() => onCompleteCard(item.card.id, boardId, !item.card.completed)}
            >
              {item.card.completed ? t('planner.markIncomplete') : t('planner.complete')}
            </Button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function PlannerDayColumn({
  day,
  planner,
  firstSchedulable,
  firstFocusBlock,
  busyAction,
  locale,
  onCreateFocusBlock,
  onMoveFocusBlock,
  onResizeFocusBlock,
  onLinkCardToFocusBlock,
  onRemoveLinkedCard,
  onDeleteFocusBlock,
  onCompleteCard,
  timeZone,
}: {
  readonly day: PlannerDay;
  readonly planner: Planner;
  readonly firstSchedulable: PlannerSchedulableCard | undefined;
  readonly firstFocusBlock: PlannerFocusBlock | undefined;
  readonly busyAction: string | null;
  readonly locale: string;
  readonly onCreateFocusBlock: (cardId: string, startAt: string, endAt: string) => void;
  readonly onMoveFocusBlock: (
    focusBlockId: string,
    startAt: string,
    endAt: string,
  ) => void;
  readonly onResizeFocusBlock: (
    focusBlockId: string,
    startAt: string,
    endAt: string,
  ) => void;
  readonly onLinkCardToFocusBlock: (focusBlockId: string, cardId: string) => void;
  readonly onRemoveLinkedCard: (focusBlockId: string, linkId: string) => void;
  readonly onDeleteFocusBlock: (focusBlockId: string) => void;
  readonly onCompleteCard: (
    cardId: string,
    boardId: string | null,
    completed: boolean,
  ) => void;
  readonly timeZone: string;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const externalEvents = planner.externalEvents.filter((event) =>
    sameDay(event.startAt, day.key, timeZone),
  );
  const focusBlocks = planner.focusBlocks.filter((block) =>
    sameDay(block.startAt, day.key, timeZone),
  );
  const slotHours = [
    ...new Set([
      ...SLOT_HOURS,
      ...externalEvents.map((event) => hourOf(event.startAt, timeZone)),
      ...focusBlocks.map((block) => hourOf(block.startAt, timeZone)),
    ]),
  ].sort((left, right) => left - right);
  const openSlotStart = instantForDateKeyHour(day.key, 10, timeZone);
  const openSlotEnd = addMinutes(openSlotStart, 60);

  return (
    <CardSurface variant="sheet" className="min-w-0 p-3">
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-ink">{day.label}</h2>
        <p className="text-xs text-ink-muted">
          {t(
            'planner.focus.blockCount',
            { count: focusBlocks.length },
            focusBlocks.length,
          )}
        </p>
      </div>
      <div className="grid gap-2">
        {slotHours.map((hour) => {
          const blocksInSlot = focusBlocks.filter(
            (block) => hourOf(block.startAt, timeZone) === hour,
          );
          const eventsInSlot = externalEvents.filter(
            (event) => hourOf(event.startAt, timeZone) === hour,
          );
          const slotTime = formatHour(hour, locale);
          return (
            <div
              key={`${day.key}-${hour}`}
              role="group"
              aria-label={t('planner.focus.slot', {
                day: day.label,
                time: slotTime,
              })}
              className="min-h-16 rounded-card border border-border bg-surface/70 p-2 focus-within:ring-2 focus-within:ring-focus"
              onDragOver={(event) => {
                if (hasDraggedPlannerCard(event)) {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'copy';
                }
              }}
              onDrop={(event) => {
                const cardId = draggedPlannerCardId(event);
                if (cardId === null) return;
                event.preventDefault();
                const startAt = instantForDateKeyHour(day.key, hour, timeZone);
                onCreateFocusBlock(cardId, startAt, addMinutes(startAt, 60));
              }}
            >
              <p className="text-xs font-semibold text-ink-muted">{slotTime}</p>
              {hour === 10 &&
              firstSchedulable !== undefined &&
              blocksInSlot.length === 0 ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2 w-full justify-center px-2"
                  aria-label={t('planner.focus.addCardAt', {
                    card: cardTitle(firstSchedulable.card.title, t),
                    time: slotTime,
                  })}
                  title={t('planner.focus.addCardAt', {
                    card: cardTitle(firstSchedulable.card.title, t),
                    time: slotTime,
                  })}
                  iconStart={<CalendarPlus className="size-4" />}
                  onClick={() =>
                    onCreateFocusBlock(
                      firstSchedulable.card.id,
                      openSlotStart,
                      openSlotEnd,
                    )
                  }
                >
                  {t('planner.focus.addCard')}
                </Button>
              ) : null}
              {hour === 11 && firstFocusBlock !== undefined ? (
                <Button
                  variant="quiet"
                  size="sm"
                  className="mt-2 w-full justify-center px-2"
                  aria-label={t('planner.focus.moveFirstHere')}
                  title={t('planner.focus.moveFirstHere')}
                  onClick={() =>
                    onMoveFocusBlock(
                      firstFocusBlock.id,
                      instantForDateKeyHour(day.key, 11, timeZone),
                      instantForDateKeyHour(day.key, 12, timeZone),
                    )
                  }
                >
                  {t('planner.focus.move')}
                </Button>
              ) : null}
              {eventsInSlot.length > 0 ? (
                <div className="mt-2 grid gap-2">
                  {eventsInSlot.map((event) => (
                    <article
                      key={event.id}
                      aria-label={t('planner.calendar.externalEventLabel', {
                        event: event.title || t('planner.calendar.busyEvent'),
                      })}
                      className="rounded-card border border-border-strong bg-surface px-3 py-2"
                    >
                      <p className="text-sm font-semibold text-ink">
                        {event.title || t('planner.calendar.busyEvent')}
                      </p>
                      <p className="text-xs text-ink-muted">
                        {t('planner.calendar.eventDetails', {
                          provider: providerLabel(event.provider, t),
                          calendar: calendarName(planner, event.calendarId, t),
                          range: formatTimeRange(
                            event.startAt,
                            event.endAt,
                            locale,
                            timeZone,
                          ),
                        })}
                      </p>
                      <p className="text-xs font-medium text-ink-muted">
                        {t('planner.calendar.readOnlyEvent')}
                      </p>
                    </article>
                  ))}
                </div>
              ) : null}
              {blocksInSlot.length > 0 ? (
                <div
                  role="group"
                  aria-label={t('planner.focus.blocks', {
                    day: day.label,
                    time: slotTime,
                  })}
                  className={cn(
                    'mt-2 grid gap-2',
                    blocksInSlot.length > 1 && 'grid-cols-2',
                  )}
                >
                  {blocksInSlot.map((block) => (
                    <PlannerFocusBlockCard
                      key={block.id}
                      block={block}
                      schedulableCards={planner.schedulableCards}
                      busy={busyAction === block.id}
                      locale={locale}
                      timeZone={timeZone}
                      onResizeFocusBlock={onResizeFocusBlock}
                      onLinkCardToFocusBlock={onLinkCardToFocusBlock}
                      onRemoveLinkedCard={onRemoveLinkedCard}
                      onDeleteFocusBlock={onDeleteFocusBlock}
                      onCompleteCard={onCompleteCard}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </CardSurface>
  );
}

function PlannerFocusBlockCard({
  block,
  schedulableCards,
  busy,
  locale,
  timeZone,
  onResizeFocusBlock,
  onLinkCardToFocusBlock,
  onRemoveLinkedCard,
  onDeleteFocusBlock,
  onCompleteCard,
}: {
  readonly block: PlannerFocusBlock;
  readonly schedulableCards: readonly PlannerSchedulableCard[];
  readonly busy: boolean;
  readonly locale: string;
  readonly timeZone: string;
  readonly onResizeFocusBlock: (
    focusBlockId: string,
    startAt: string,
    endAt: string,
  ) => void;
  readonly onLinkCardToFocusBlock: (focusBlockId: string, cardId: string) => void;
  readonly onRemoveLinkedCard: (focusBlockId: string, linkId: string) => void;
  readonly onDeleteFocusBlock: (focusBlockId: string) => void;
  readonly onCompleteCard: (
    cardId: string,
    boardId: string | null,
    completed: boolean,
  ) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const linkedTitle =
    block.linkedCards.map((linked) => linkedCardLabel(linked, t)).join(', ') ||
    t('planner.focus.titleFallback');
  const candidate = schedulableCards.find(
    (item) => !block.linkedCards.some((linked) => linked.cardId === item.card.id),
  );
  const extendedEnd = addMinutes(block.endAt, 30);

  function handleDrop(event: DragEvent<HTMLElement>): void {
    const cardId = draggedPlannerCardId(event);
    if (cardId === null) return;
    event.preventDefault();
    event.stopPropagation();
    if (block.linkedCards.some((linked) => linked.cardId === cardId)) return;
    onLinkCardToFocusBlock(block.id, cardId);
  }

  return (
    <article
      aria-label={t('planner.focus.blockLabel', { title: linkedTitle })}
      className="min-w-0 rounded-card border border-accent bg-surface-raised p-2 shadow-card focus-within:ring-2 focus-within:ring-focus"
      onDragOver={(event) => {
        if (hasDraggedPlannerCard(event)) {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }
      }}
      onDrop={handleDrop}
    >
      <div className="flex flex-col gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink" title={linkedTitle}>
            {linkedTitle}
          </p>
          <p className="truncate text-xs text-ink-muted">
            {t('planner.focus.timeRange', {
              range: formatTimeRange(block.startAt, block.endAt, locale, timeZone),
            })}
          </p>
          <p className="truncate text-xs text-ink-muted">
            {t('planner.focus.sync', {
              status: syncLabel(block.calendarSync.status, t),
            })}
          </p>
        </div>
        <ul
          className="grid gap-1"
          aria-label={t('planner.focus.linkedCards', { title: linkedTitle })}
        >
          {block.linkedCards.map((linked) => {
            const boardDetails =
              linked.details?.source === 'board' ? linked.details : null;
            // The board id the completion route needs, or null for an Inbox
            // card — which is completable too since FR-NOTIF-024's contract
            // chunk, through PATCH /me/inbox/cards/:id/completed.
            const completable =
              linked.details?.card.canComplete === true
                ? {
                    card: linked.details.card,
                    boardId: boardDetails === null ? null : boardDetails.board.id,
                  }
                : null;
            return (
              <li
                // ⚠ The LINK row's id, not the card's. Two orphaned links on
                // one block both carry `cardId: null`, and the previous
                // `linked.cardId ?? block.id` gave them the same React key.
                key={linked.linkId}
                className="flex min-w-0 items-center gap-1 text-xs text-ink"
              >
                <span className="grid min-w-0 flex-1 gap-0.5">
                  <span
                    className={cn(
                      'truncate',
                      linked.state === 'completed' && 'line-through',
                    )}
                    title={linkedCardLabel(linked, t)}
                  >
                    {linkedCardLabel(linked, t)}
                  </span>
                  <span className="text-ink-muted">
                    ({linkedCardStateLabel(linked, t)})
                  </span>
                </span>
                {linked.state === 'inactive' ||
                linked.state === 'inaccessible' ||
                linked.state === 'orphaned' ? (
                  <IconButton
                    label={t('planner.focus.remove')}
                    size="sm"
                    title={t('planner.focus.remove')}
                    // ⚠ Always the link-scoped removal, never
                    // `onDeleteFocusBlock` — which is what an orphaned link
                    // used to fall back to, destroying the block and every
                    // other card on it. FR-NOTIF-023 asks for the stale link
                    // to go; FR-NOTIF-024's edge case says the reserved time
                    // stays.
                    onClick={() => onRemoveLinkedCard(block.id, linked.linkId)}
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                ) : completable !== null ? (
                  <IconButton
                    label={
                      completable.card.completed
                        ? t('planner.markIncomplete')
                        : t('planner.complete')
                    }
                    size="sm"
                    title={
                      completable.card.completed
                        ? t('planner.markIncomplete')
                        : t('planner.complete')
                    }
                    onClick={() =>
                      onCompleteCard(
                        completable.card.id,
                        completable.boardId,
                        !completable.card.completed,
                      )
                    }
                  >
                    <Check className="size-4" />
                  </IconButton>
                ) : null}
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap gap-1">
          <IconButton
            label={t('planner.focus.extend')}
            size="sm"
            title={t('planner.focus.extend')}
            disabled={busy}
            onClick={() =>
              onResizeFocusBlock(block.id, toIsoDateTime(block.startAt), extendedEnd)
            }
          >
            <RefreshCcw className="size-4" />
          </IconButton>
          {candidate !== undefined ? (
            <IconButton
              label={t('planner.focus.linkCard', {
                card: cardTitle(candidate.card.title, t),
              })}
              size="sm"
              title={t('planner.focus.linkCard', {
                card: cardTitle(candidate.card.title, t),
              })}
              onClick={() => onLinkCardToFocusBlock(block.id, candidate.card.id)}
            >
              <LinkIcon className="size-4" />
            </IconButton>
          ) : null}
          <IconButton
            label={t('planner.focus.delete')}
            size="sm"
            title={t('planner.focus.delete')}
            onClick={() => onDeleteFocusBlock(block.id)}
          >
            <Trash2 className="size-4" />
          </IconButton>
        </div>
      </div>
    </article>
  );
}

function PlannerPreferencesPanel({
  preferences,
  onWorkingHoursChange,
  onWorkingDaysChange,
}: {
  readonly preferences: PlannerPreferences;
  readonly onWorkingHoursChange: (
    workingHours: PlannerPreferences['workingHours'],
  ) => void;
  readonly onWorkingDaysChange: (workingDays: readonly PlannerWeekday[]) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  const { language } = useLocalePreferencesValue();
  const workingDays = new Set(preferences.workingDays);
  const weekdays = orderedWeekdays(weekStartForLocale(language));
  return (
    <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
      <h2 className="text-base font-semibold text-ink">{t('planner.rules.title')}</h2>
      <div className="grid grid-cols-2 gap-2">
        <TextField
          label={t('planner.rules.workingDayStarts')}
          value={preferences.workingHours.start.slice(0, 5)}
          onChange={(event) =>
            onWorkingHoursChange({
              ...preferences.workingHours,
              start: event.currentTarget.value,
            })
          }
        />
        <TextField
          label={t('planner.rules.workingDayEnds')}
          value={preferences.workingHours.end.slice(0, 5)}
          onChange={(event) =>
            onWorkingHoursChange({
              ...preferences.workingHours,
              end: event.currentTarget.value,
            })
          }
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {weekdays.map((day) => (
          <label
            key={day}
            className="flex items-center gap-2 text-sm capitalize text-ink"
          >
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-accent)]"
              checked={workingDays.has(day)}
              onChange={(event) => {
                const next = new Set(workingDays);
                if (event.currentTarget.checked) {
                  next.add(day);
                } else if (next.size > 1) {
                  next.delete(day);
                }
                onWorkingDaysChange([...next]);
              }}
            />
            {t(`planner.weekday.${day}`)}
          </label>
        ))}
      </div>
    </CardSurface>
  );
}

function PlannerCalendarAccountsPanel({
  planner,
  onStartCalendarConnection,
  onToggleCalendarDisplayed,
  onDisconnectCalendarAccount,
}: {
  readonly planner: Planner;
  readonly onStartCalendarConnection: (provider: CalendarProvider) => void;
  readonly onToggleCalendarDisplayed: (calendarId: string, displayed: boolean) => void;
  readonly onDisconnectCalendarAccount: (accountId: string) => void;
}): ReactElement {
  const { t } = useInterfaceLanguage();
  return (
    <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
      <div>
        <h2 className="text-base font-semibold text-ink">
          {t('planner.calendar.title')}
        </h2>
        <p className="text-sm text-ink-muted">{t('planner.calendar.description')}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onStartCalendarConnection('google')}
        >
          {t('planner.calendar.connect', { provider: providerLabel('google', t) })}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onStartCalendarConnection('outlook')}
        >
          {t('planner.calendar.connect', { provider: providerLabel('outlook', t) })}
        </Button>
      </div>
      {planner.connectedCalendarAccounts.length >= 10 ? (
        <Notice tone="info" body={t('planner.calendar.limitReached')} />
      ) : null}
      {planner.connectedCalendarAccounts.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('planner.calendar.emptyAccounts')}</p>
      ) : (
        <ul className="grid gap-2" aria-label={t('planner.calendar.accountList')}>
          {planner.connectedCalendarAccounts.map((account) => (
            <li
              key={account.id}
              className="rounded-card border border-border bg-surface px-3 py-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-ink">
                    {t('planner.calendar.providerCalendar', {
                      provider: providerLabel(account.provider, t),
                    })}
                  </p>
                  <p className="break-all text-xs text-ink-muted">
                    {account.label ?? account.externalAccountId}
                  </p>
                </div>
                <Button
                  variant="quiet"
                  size="sm"
                  onClick={() => onDisconnectCalendarAccount(account.id)}
                >
                  {t('planner.calendar.disconnect')}
                </Button>
              </div>
              <div className="mt-2 grid gap-1">
                {planner.connectedCalendars
                  .filter((calendar) => calendar.accountId === account.id)
                  .map((calendar) => (
                    <label
                      key={calendar.id}
                      className="flex items-center gap-2 text-sm text-ink"
                    >
                      <input
                        type="checkbox"
                        className="size-4 accent-[var(--color-accent)]"
                        checked={calendar.displayed}
                        onChange={(event) =>
                          onToggleCalendarDisplayed(
                            calendar.id,
                            event.currentTarget.checked,
                          )
                        }
                      />
                      <span>{calendar.name}</span>
                      {!calendar.writable ? (
                        <span className="text-xs text-ink-muted">
                          {t('planner.calendar.readOnly')}
                        </span>
                      ) : null}
                    </label>
                  ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </CardSurface>
  );
}

function PlannerSuggestionsPanel({
  planner,
  suggestions,
  busy,
  timeZone,
  onRequestSuggestions,
  onAcceptSuggestion,
  onRejectSuggestion,
}: {
  readonly planner: Planner;
  readonly suggestions: PlannerSuggestions | undefined;
  readonly busy: boolean;
  readonly timeZone: string;
  readonly onRequestSuggestions: (sourceBoardIds: readonly string[] | undefined) => void;
  readonly onAcceptSuggestion: (suggestion: PlannerSuggestion) => void;
  readonly onRejectSuggestion: (proposalId: string) => void;
}): ReactElement {
  const { t, locale } = useInterfaceLanguage();
  const previewBoardOptions = plannerBoardOptions(planner);
  // ⚠ Held as the EXCLUDED set, not the selected one. Every board starts
  // selected, so a board that appears later — a card gains a due date, a linked
  // card resolves — is selected too. A selected-set seeded from
  // `previewBoardOptions` would silently leave newcomers out of the proposals a
  // user believes are unconstrained.
  //
  // ⚠ Deliberately NOT `planner.selectedBoardFilters`. Sending the stored filter
  // as `sourceBoardIds` would be a no-op — the server already applies it — and
  // would make a browser assertion green without the server ever reading the
  // new field.
  const [excludedPreviewBoardIds, setExcludedPreviewBoardIds] = useState<
    ReadonlySet<string>
  >(() => new Set<string>());
  const previewBoardIds = previewBoardOptions
    .filter((board) => !excludedPreviewBoardIds.has(board.id))
    .map((board) => board.id);
  const allPreviewBoardsSelected = previewBoardIds.length === previewBoardOptions.length;
  // Three UI states (all / some / none) must not collapse onto two wire states.
  // `sourceBoardIds` is `.min(1)`, so "none" has no honest encoding: omitting
  // the field means *no constraint*, the opposite of what the user did, and `[]`
  // is a 422 the user did not cause. Disabling the button lies about nothing.
  const noPreviewBoardSelected =
    previewBoardOptions.length > 0 && previewBoardIds.length === 0;

  function togglePreviewBoard(boardId: string): void {
    setExcludedPreviewBoardIds((current) => {
      const next = new Set(current);
      if (next.has(boardId)) {
        next.delete(boardId);
      } else {
        next.add(boardId);
      }
      return next;
    });
  }

  return (
    <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
      <div>
        <h2 id={SMART_SCHEDULE_HEADING_ID} className="text-base font-semibold text-ink">
          {t('planner.smartSchedule.title')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('planner.smartSchedule.description', {
            start: planner.preferences.workingHours.start.slice(0, 5),
            end: planner.preferences.workingHours.end.slice(0, 5),
          })}
        </p>
      </div>
      {previewBoardOptions.length > 0 ? (
        // Named "Smart schedule Board filters" by the two ids together, so the
        // proposal-scoped list and the panel filter two panels above are
        // distinguishable to a screen reader while the visible copy stays the
        // one word the B80 catalogue already carries — no new key.
        //
        // ⚠ BOTH ids are load bearing. Drop the heading one and this group is
        // named "Board filters" too, which is not only ambiguous aloud: it also
        // makes `getByRole('group', { name: 'Board filters' })` match twice, so
        // the panel-filter test and all three proposal-scope tests fail at once
        // with "found multiple elements".
        <fieldset
          aria-labelledby={`${SMART_SCHEDULE_HEADING_ID} ${SMART_SCHEDULE_BOARDS_LEGEND_ID}`}
          className="m-0 grid gap-2 border-0 p-0"
        >
          <legend
            id={SMART_SCHEDULE_BOARDS_LEGEND_ID}
            className="pb-1 text-sm font-medium text-ink"
          >
            {t('planner.boardFilters.title')}
          </legend>
          {previewBoardOptions.map((board) => (
            <label
              key={board.id}
              className="flex items-center gap-2 rounded-card border border-border-strong bg-surface px-3 py-2 text-sm text-ink"
            >
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-accent)]"
                checked={!excludedPreviewBoardIds.has(board.id)}
                onChange={() => togglePreviewBoard(board.id)}
              />
              <span className="min-w-0 flex-1 truncate">{board.title}</span>
            </label>
          ))}
        </fieldset>
      ) : null}
      <Button
        variant="primary"
        size="sm"
        disabled={busy || noPreviewBoardSelected}
        iconStart={<RefreshCcw className="size-4" />}
        onClick={() =>
          onRequestSuggestions(allPreviewBoardsSelected ? undefined : previewBoardIds)
        }
      >
        {t('planner.smartSchedule.title')}
      </Button>
      {suggestions?.suggestions.length === 0 ? (
        <Notice tone="info" body={t('planner.smartSchedule.empty')} />
      ) : null}
      {suggestions !== undefined && suggestions.suggestions.length > 0 ? (
        <ul className="grid gap-2" aria-label={t('planner.smartSchedule.previews')}>
          {suggestions.suggestions.map((suggestion) => (
            <li
              key={suggestion.proposalId}
              className="rounded-card border border-dashed border-accent bg-surface px-3 py-2"
            >
              <p className="text-sm font-semibold text-ink">
                {t('planner.smartSchedule.preview', {
                  range: formatTimeRange(
                    suggestion.startAt,
                    suggestion.endAt,
                    locale,
                    timeZone,
                  ),
                })}
              </p>
              <p className="text-xs text-ink-muted">
                {suggestion.reason ?? t('planner.smartSchedule.reasonFallback')}
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onAcceptSuggestion(suggestion)}
                >
                  {t('planner.smartSchedule.accept')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onRejectSuggestion(suggestion.proposalId)}
                >
                  {t('planner.smartSchedule.reject')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </CardSurface>
  );
}

function plannerBoardOptions(
  planner: Planner | undefined,
): { id: string; title: string }[] {
  if (planner === undefined) return [];
  const map = new Map<string, string>();
  for (const item of planner.schedulableCards) {
    if (item.source === 'board') map.set(item.board.id, item.board.title);
  }
  for (const block of planner.focusBlocks) {
    for (const linked of block.linkedCards) {
      if (linked.details?.source === 'board') {
        map.set(linked.details.board.id, linked.details.board.title);
      }
    }
  }
  return [...map].map(([id, title]) => ({ id, title }));
}

function calendarName(
  planner: Planner,
  calendarId: string,
  t: (key: string, values?: Record<string, string | number>, count?: number) => string,
): string {
  return (
    planner.connectedCalendars.find((calendar) => calendar.id === calendarId)?.name ??
    t('planner.calendar.fallback')
  );
}

function providerLabel(
  provider: ConnectedCalendarProvider,
  t: InterfaceTranslate,
): string {
  return t(
    provider === 'google'
      ? 'planner.calendar.provider.google'
      : 'planner.calendar.provider.outlook',
  );
}

function linkedCardLabel(
  card: PlannerLinkedCard,
  t: (key: string, values?: Record<string, string | number>, count?: number) => string,
): string {
  if (card.details === null) return t('planner.focus.unavailableCard');
  return cardTitle(card.details.card.title, t);
}

function linkedCardStateLabel(
  card: PlannerLinkedCard,
  t: (key: string, values?: Record<string, string | number>, count?: number) => string,
): string {
  switch (card.state) {
    case 'live':
      return t('planner.focus.state.live');
    case 'completed':
      return t('planner.focus.state.completed');
    case 'inactive':
      return card.reason === 'archived'
        ? t('planner.focus.state.archived')
        : t('planner.focus.state.deleted');
    case 'inaccessible':
      return t('planner.focus.state.inaccessible');
    case 'orphaned':
      return t('planner.focus.state.orphaned');
  }
}

function syncLabel(
  status: PlannerFocusBlock['calendarSync']['status'],
  t: (key: string, values?: Record<string, string | number>, count?: number) => string,
): string {
  return t(`planner.focus.sync.${status}`);
}

function cardTitle(
  title: string,
  t: (key: string, values?: Record<string, string | number>, count?: number) => string,
): string {
  return title.trim() === '' ? t('planner.untitledCard') : title;
}

function formatDateTime(value: string, locale: string, timeZone: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(value));
}

function formatTimeRange(
  startAt: string,
  endAt: string,
  locale: string,
  timeZone: string,
): string {
  const formatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
  return `${formatter.format(new Date(startAt))}-${formatter.format(new Date(endAt))}`;
}

function formatHour(hour: number, locale: string): string {
  const date = new Date(Date.UTC(2026, 0, 1, hour, 0, 0, 0));
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(date);
}

function sameDay(left: string, dayKey: string, timeZone: string): boolean {
  return dateKeyInZone(left, timeZone) === dayKey;
}

function hourOf(source: string, timeZone: string): number {
  return Number(localDateTimeParts(source, timeZone).hour);
}

function dateKeyInZone(source: string, timeZone: string): string {
  const parts = localDateTimeParts(source, timeZone);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function instantForDateKeyHour(dateKey: string, hour: number, timeZone: string): string {
  const [year, month, day] = dateKey.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    throw new RangeError(`Invalid Planner date key: ${dateKey}`);
  }
  const targetAsUtc = Date.UTC(year, month - 1, day, hour, 0, 0, 0);
  let instant = targetAsUtc;
  for (let index = 0; index < 4; index += 1) {
    const next = targetAsUtc - timezoneOffsetMs(instant, timeZone);
    if (next === instant) break;
    instant = next;
  }
  return new Date(instant).toISOString();
}

function addMinutes(source: string, minutes: number): string {
  return new Date(new Date(source).getTime() + minutes * 60_000).toISOString();
}

function toIsoDateTime(source: string): string {
  return new Date(source).toISOString();
}

function draggedPlannerCardId(event: DragEvent<HTMLElement>): string | null {
  const cardId =
    event.dataTransfer.getData(PLANNER_CARD_DRAG_TYPE) ||
    event.dataTransfer.getData('text/plain');
  const trimmed = cardId.trim();
  return trimmed === '' ? null : trimmed;
}

function hasDraggedPlannerCard(event: DragEvent<HTMLElement>): boolean {
  return Array.from(event.dataTransfer.types).includes(PLANNER_CARD_DRAG_TYPE);
}

function timezoneOffsetMs(instant: number, timeZone: string): number {
  const parts = localDateTimeParts(new Date(instant).toISOString(), timeZone);
  const localAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return localAsUtc - instant;
}

function localDateTimeParts(
  source: string,
  timeZone: string,
): {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
} {
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).formatToParts(new Date(source));
  const part = (type: Intl.DateTimeFormatPartTypes): string => {
    const value = parts.find((candidate) => candidate.type === type)?.value;
    if (value === undefined) {
      throw new RangeError(`Missing ${type} date-time part`);
    }
    return value;
  };
  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: part('hour'),
    minute: part('minute'),
    second: part('second'),
  };
}
